/**
 * What the Weighted claims switch says about where its figures come from (docs/RULES.md,
 * "Who applies the rules: the model, with the app as fallback"):
 *
 *   the model, current          "From the model, rules as of 6 Oct 2026"
 *   the model, rules changed     + "Rules changed after the last refresh; the next refresh applies them"
 *   no model yet                 "Preview: not yet in the model · weighted by 2 rules"
 *
 * Pure, so the three cases are tested without the app around them.
 */
import type { WeightsState } from './weights'

export const PREVIEW_LABEL = 'Preview: not yet in the model'
export const STALE_NOTICE = 'Rules changed after the last refresh; the next refresh applies them'

type Ok = Extract<WeightsState, { status: 'ok' }>

const day = (iso: string | null) => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

function rulesText(r: Ok['rules']): string {
  const parts = [
    r.coinsurancePayers > 0 && `${r.coinsurancePayers} coinsurance payer rule${r.coinsurancePayers === 1 ? '' : 's'}`,
    r.hardPayers > 0 && `${r.hardPayers} hard payer rule${r.hardPayers === 1 ? '' : 's'}`,
  ].filter(Boolean)
  return parts.length ? parts.join(' and ') : 'no active rules (same as standard)'
}

/** The note beside the switch: `on` is whether it is on. */
export function weightsNote(w: WeightsState, on: boolean): string {
  if (w.status === 'loading') return 'Loading the claim rules…'
  if (w.status === 'unavailable') return `Standard only: ${w.reason}`
  if (!on) return 'Every billed claim counts 1'
  if (w.source === 'preview') return `${PREVIEW_LABEL} · weighted by ${rulesText(w.rules)}`
  const asOf = day(w.rulesVersion)
  const from = asOf ? `From the model, rules as of ${asOf}` : 'From the model'
  return w.stale ? `${from} · ${STALE_NOTICE}` : from
}

/** The masthead tile's label and hover while Weighted is on. */
export function weightedTile(w: WeightsState): { label: string; title: string } {
  if (w.status === 'ok' && w.source === 'preview') {
    return { label: 'Weighted claims (preview)', title: `${PREVIEW_LABEL}: counted by the app from the claim rules` }
  }
  const stale = w.status === 'ok' && w.stale ? ` · ${STALE_NOTICE}` : ''
  return { label: 'Weighted claims', title: `Billed claims counted by the claim rules, as the model applied them${stale}` }
}
